<template>
  <!-- FT-191 REQ-07: экран блокировки общего компьютера. Непрозрачный и выше диалогов:
       страница прежнего сотрудника остаётся смонтированной под ним (ASM-01). Карточка — в языке
       формы входа (Signin: elevation-12, primary-тулбар), PIN — в ячейках, как код из письма. -->
  <div
    class="lock-screen"
    :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
    data-test="lock-screen"
    data-device-overlay
    role="dialog"
    aria-modal="true"
    :aria-label="$t('device_lock.title')"
  >
    <v-card class="lock-screen__card elevation-12">
      <v-toolbar dark color="primary" flat class="lock-screen__toolbar">
        <v-icon left>mdi-lock-outline</v-icon>
        <div class="lock-screen__heading">
          <div class="lock-screen__title">{{ $t("device_lock.title") }}</div>
          <div v-if="device && device.name" class="lock-screen__device">{{ device.name }}</div>
        </div>
        <v-spacer />
        <div class="lock-screen__clock" aria-hidden="true">{{ clock }}</div>
      </v-toolbar>

      <v-card-text class="pt-5 pb-2">
        <div class="lock-screen__label">{{ $t("device_lock.choose_employee") }}</div>
        <!-- До 6 сотрудников — плитки; больше — поиск и компактный список: на 10+ искать себя
             глазами среди плиток долго, листание страницами тоже. -->
        <v-text-field
          v-if="compact"
          ref="search"
          v-model="query"
          data-test="lock-search"
          class="lock-screen__search"
          outlined
          dense
          hide-details
          clearable
          autocomplete="off"
          prepend-inner-icon="mdi-magnify"
          :placeholder="$t('device_lock.search')"
          @keydown.enter.prevent="selectFirstMatch"
        />
        <div
          ref="userList"
          class="lock-screen__users"
          :class="{ 'lock-screen__users--list': compact }"
          role="radiogroup"
          :aria-label="$t('device_lock.choose_employee')"
        >
          <v-btn
            v-for="user in visibleUsers"
            :key="user.id"
            data-test="lock-user"
            class="lock-user"
            :class="{ 'lock-user--selected': user.id === selectedUserId, 'lock-user--row': compact }"
            :color="user.id === selectedUserId ? 'primary' : undefined"
            :outlined="user.id === selectedUserId"
            depressed
            role="radio"
            :aria-checked="String(user.id === selectedUserId)"
            :title="user.name"
            @click="selectUser(user.id)"
          >
            <span class="lock-user__inner">
              <v-avatar :size="compact ? 32 : 40" :class="avatarClass(user)">
                <img v-if="user.avatar_url && !brokenAvatars[user.id]" :src="user.avatar_url" alt="" @error="markBroken(user.id)" />
                <span v-else :class="avatarTextClass(user)" class="lock-user__initials">{{ initials(user.name) }}</span>
              </v-avatar>
              <span class="lock-user__name">{{ user.name }}</span>
              <v-icon v-if="user.id === selectedUserId" small color="primary" class="lock-user__check">mdi-check-circle</v-icon>
            </span>
          </v-btn>
          <div v-if="!visibleUsers.length" class="lock-screen__empty">{{ $t("device_lock.no_match") }}</div>
        </div>

        <v-expand-transition>
          <v-alert v-if="switchWarning" type="warning" dense text class="mt-3 mb-0" icon="mdi-account-switch-outline">
            {{ $t("device_lock.switch_warning", { name: lockedUserName }) }}
          </v-alert>
        </v-expand-transition>

        <!-- У выбранного сотрудника PIN нет (не успел задать, нажал «Позже», сбросил) — ячейки
             бессмысленны: сразу путь к входу по коду. -->
        <div v-if="selectedWithoutPin" class="lock-screen__no-pin" data-test="lock-no-pin">
          <v-alert type="info" text dense icon="mdi-dialpad" class="mt-4 mb-3">{{ $t("device_lock.no_pin") }}</v-alert>
          <v-btn color="primary" block large depressed data-test="lock-sign-in-code" @click="signInWithCode">
            <v-icon left>mdi-email-outline</v-icon>{{ $t("device_lock.sign_in_with_code") }}
          </v-btn>
        </div>
        <form v-else class="lock-screen__form" @submit.prevent="submit">
          <label class="lock-screen__label" for="lock-screen-pin">{{ $t("device_lock.enter_pin") }}</label>
          <!-- Настоящее поле лежит прозрачным слоем над ячейками: печать с клавиатуры, вставка,
               автофокус и Enter работают как у обычного input; ячейки только показывают длину. -->
          <div class="lock-pin" :class="{ 'lock-pin--error': shake, 'lock-pin--disabled': pinBlocked }" @click="focusPin">
            <input
              id="lock-screen-pin"
              ref="pin"
              v-model="pin"
              data-test="lock-pin"
              class="lock-pin__input"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              data-lpignore="true"
              data-1p-ignore
              data-bwignore="true"
              data-form-type="other"
              maxlength="4"
              :disabled="loading || pinBlocked"
              :aria-describedby="errorKey ? 'lock-screen-error' : undefined"
              @input="onPinInput"
              @focus="pinFocused = true"
              @blur="pinFocused = false"
            />
            <span
              v-for="slot in slotCount"
              :key="slot"
              class="lock-pin__slot"
              :class="{
                'lock-pin__slot--filled': slot <= pin.length,
                'lock-pin__slot--active': slot === pin.length + 1 && pinFocused,
              }"
              aria-hidden="true"
            >
              <span v-if="slot <= pin.length" class="lock-pin__dot" />
            </span>
          </div>

          <!-- Строка ошибки зарезервирована всегда: карточка не прыгает при неверном PIN. -->
          <div id="lock-screen-error" class="lock-screen__error" role="alert">
            <v-progress-circular v-if="loading" indeterminate size="18" width="2" color="primary" data-test="lock-checking" />
            <span v-else-if="errorKey" data-test="lock-error">
              <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>{{ $t(errorKey, errorParams) }}
            </span>
          </div>

          <div class="lock-keypad" aria-hidden="true">
            <v-btn
              v-for="key in keypad"
              :key="key"
              class="lock-keypad__key"
              :class="{ 'lock-keypad__key--blank': key === '' }"
              text
              tabindex="-1"
              :disabled="key === '' || loading || pinBlocked"
              @click="pressKey(key)"
            >
              <v-icon v-if="key === 'erase'">mdi-backspace-outline</v-icon>
              <template v-else>{{ key }}</template>
            </v-btn>
          </div>

          <!-- Кнопки «Разблокировать» нет: как код из письма — четвёртая цифра сама отправляет PIN. -->
          <div class="lock-screen__forgot">
            <a href="#" data-test="lock-forgot" @click.prevent="signInWithCode">{{ $t("device_lock.forgot_pin") }}</a>
          </div>
        </form>
      </v-card-text>

      <v-divider />
      <v-card-actions class="lock-screen__footer">
        <v-btn text color="primary" data-test="lock-other" @click="otherEmployee">
          <v-icon left>mdi-account-plus-outline</v-icon>{{ $t("device_lock.other_employee") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { resetToSignin as defaultResetToSignin, isolateOverlay, PIN_FORMAT } from "@/utils/deviceLock";

// Палитра аватаров без фото: цвет по id стабилен между блокировками — человек узнаёт свою плитку.
const AVATAR_COLORS = [ "blue", "teal", "deep-purple", "orange", "pink", "indigo", "green", "brown" ];
const MAX_PIN = 4; // ровно 4 цифры (решение USER 2026-10-09)
const TILE_LIMIT = 6;

function normalizeName(value) {
  return String(value || "").trim().toLowerCase().replace(/ё/g, "е");
}

export default {
  name: "LockScreen",
  props: {
    // Подменяется в тестах: настоящий уводит страницу полной перезагрузкой.
    resetToSignin: { type: Function, default: () => defaultResetToSignin() },
  },
  data() {
    return {
      selectedUserId: this.$store.getters["deviceLock/lockedUserId"],
      pin: "",
      loading: false,
      errorKey: null,
      errorParams: null,
      // Блокировки PIN по сотруднику (pin_locked / pin_disabled): переживают смену выбора и поиск;
      // pin_locked снимается сам по locked_until (проверка — с тиком часов).
      blocked: {},
      shake: false,
      pinFocused: false,
      brokenAvatars: {},
      clock: "",
      clockTimer: null,
      keypad: [ "1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "erase" ],
      query: "",
    };
  },
  computed: {
    users() {
      return this.$store.getters["deviceLock/lockScreenUsers"];
    },
    compact() {
      return this.users.length > TILE_LIMIT;
    },
    // Поиск по части имени без учёта регистра и ё/е.
    visibleUsers() {
      const q = normalizeName(this.query);
      if (!this.compact || !q) return this.users;
      return this.users.filter((u) => normalizeName(u.name).includes(q));
    },
    device() {
      return this.$store.getters["deviceLock/device"];
    },
    lockedUserId() {
      return this.$store.getters["deviceLock/lockedUserId"];
    },
    lockedUserName() {
      const user = this.users.find((u) => u.id === this.lockedUserId);
      return user ? user.name : "";
    },
    // OQ-02: при смене на другого сотрудника работа прежнего закрывается — предупреждаем всегда.
    switchWarning() {
      return this.lockedUserId != null && this.selectedUserId !== this.lockedUserId;
    },
    pinBlocked() {
      return this.selectedUserId != null && !!this.blocked[this.selectedUserId];
    },
    pinValid() {
      return PIN_FORMAT.test(this.pin);
    },
    slotCount() {
      return MAX_PIN;
    },
    selectedUser() {
      return this.users.find((u) => u.id === this.selectedUserId) || null;
    },
    // pin_set приходит с сервера (app_version); нет поля — считаем, что PIN есть (старый ответ API).
    selectedWithoutPin() {
      return !!this.selectedUser && this.selectedUser.pin_set === false;
    },
  },
  watch: {
    // Выбранный сотрудник, скрытый поиском, не должен получить чужой PIN: выбор снимается, PIN
    // отправится только после явного выбора (клик или Enter в поиске).
    query() {
      this.scrollListToTop();
      if (this.selectedUserId != null && !this.visibleUsers.some((u) => u.id === this.selectedUserId)) {
        this.selectedUserId = null;
        this.pin = "";
        this.errorKey = null;
      }
    },
  },
  mounted() {
    this.restorePage = isolateOverlay(this.$el);
    // Единственный сотрудник списка — сразу выбран, остаётся PIN.
    if (this.selectedUserId == null && this.users.length === 1) this.selectedUserId = this.users[0].id;
    this.tickClock();
    this.clockTimer = setInterval(this.tickClock, 15000);
    this.scrollListToTop(); // последний работавший стоит первым — список открывается с начала
    // В длинном списке без выбранного сотрудника первым делом ищут себя, иначе — сразу PIN.
    this.$nextTick(this.compact && this.selectedUserId == null ? this.focusSearch : this.focusPin);
  },
  beforeDestroy() {
    clearInterval(this.clockTimer);
    if (this.restorePage) this.restorePage();
  },
  methods: {
    selectUser(id) {
      // Повторный выбор того же сотрудника не снимает действующую блокировку PIN.
      if (id === this.selectedUserId) {
        this.$nextTick(this.focusPin);
        return;
      }
      this.selectedUserId = id;
      const block = this.blocked[id];
      this.errorKey = block ? block.errorKey : null;
      this.errorParams = block ? block.errorParams : null;
      this.pin = "";
      this.$nextTick(this.focusPin);
    },
    scrollListToTop() {
      this.$nextTick(() => {
        if (this.$refs.userList) this.$refs.userList.scrollTop = 0;
      });
    },
    selectFirstMatch() {
      if (this.visibleUsers.length) this.selectUser(this.visibleUsers[0].id);
    },
    focusSearch() {
      if (this.$refs.search && typeof this.$refs.search.focus === "function") this.$refs.search.focus();
    },
    focusPin() {
      if (this.$refs.pin && typeof this.$refs.pin.focus === "function") this.$refs.pin.focus();
    },
    // Только цифры: вставка «48 21» или буквы с клавиатуры не превращаются в неверную попытку.
    onPinInput() {
      const digits = this.pin.replace(/\D/g, "").slice(0, MAX_PIN);
      if (digits !== this.pin) this.pin = digits;
      if (this.errorKey === "device_lock.invalid_pin") this.errorKey = null;
      if (this.pin.length === MAX_PIN) this.submit();
    },
    pressKey(key) {
      if (key === "erase") {
        this.pin = this.pin.slice(0, -1);
      } else if (this.pin.length < MAX_PIN) {
        this.pin += key;
      }
      this.onPinInput();
      this.focusPin();
    },
    async submit() {
      if (!this.pinValid || this.loading || this.pinBlocked || this.selectedUserId == null) return;
      this.loading = true;
      this.errorKey = null;
      try {
        await this.$store.dispatch("deviceLock/unlock", { userId: this.selectedUserId, pin: this.pin });
      } catch (error) {
        this.pin = "";
        this.handleError(error);
      } finally {
        this.loading = false;
        if (!this.pinBlocked) this.$nextTick(this.focusPin);
      }
    },
    handleError(error) {
      const code = error && error.code;
      if (code === "device_not_active") {
        this.resetToSignin();
        return;
      }
      this.errorParams = null;
      if (code === "pin_locked") {
        this.errorKey = "device_lock.pin_locked_until";
        this.errorParams = { time: error.lockedUntil ? new Date(error.lockedUntil).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "" };
        this.blockSelected(error.lockedUntil ? new Date(error.lockedUntil).getTime() : null);
      } else if (code === "pin_disabled") {
        this.errorKey = "device_lock.pin_disabled";
        this.blockSelected(null);
      } else if (code === "invalid_pin") {
        this.errorKey = "device_lock.invalid_pin";
        this.triggerShake();
      } else {
        this.errorKey = "device_lock.unlock_failed";
      }
    },
    triggerShake() {
      this.shake = false;
      this.$nextTick(() => {
        this.shake = true;
        setTimeout(() => { this.shake = false; }, 450);
      });
    },
    // Забыл PIN или PIN не задан — полный вход по коду из письма или ключом доступа.
    signInWithCode() {
      this.resetToSignin();
    },
    otherEmployee() {
      this.resetToSignin();
    },
    blockSelected(until) {
      this.$set(this.blocked, this.selectedUserId, { until, errorKey: this.errorKey, errorParams: this.errorParams });
    },
    tickClock() {
      this.clock = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      const now = Date.now();
      Object.keys(this.blocked).forEach((id) => {
        const block = this.blocked[id];
        if (!block.until || block.until > now) return;
        this.$delete(this.blocked, id);
        if (String(this.selectedUserId) === id) this.errorKey = null;
      });
    },
    initials(name) {
      const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
      return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join("") || "?";
    },
    colorOf(user) {
      return AVATAR_COLORS[Math.abs(Number(user.id) || 0) % AVATAR_COLORS.length];
    },
    avatarClass(user) {
      return user.avatar_url && !this.brokenAvatars[user.id] ? "" : `${this.colorOf(user)} lighten-4`;
    },
    avatarTextClass(user) {
      return `${this.colorOf(user)}--text text--darken-3`;
    },
    markBroken(id) {
      this.$set(this.brokenAvatars, id, true);
    },
  },
};
</script>

<style lang="scss" scoped>
.lock-screen {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  padding: 24px 16px;
  overflow-y: auto;
}

// margin: auto, а не align-items: center: карточка выше окна прокручивается, а не обрезается сверху.
.lock-screen__card {
  margin: auto;
  width: 100%;
  max-width: 440px;
  border-radius: 8px !important;
  overflow: hidden;
}

.lock-screen__toolbar ::v-deep .v-toolbar__content {
  height: auto !important;
  min-height: 64px;
  padding-top: 10px;
  padding-bottom: 10px;
}

.lock-screen__heading {
  min-width: 0;
}

.lock-screen__title {
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.3;
}

.lock-screen__device {
  font-size: 0.8125rem;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lock-screen__clock {
  font-size: 1.5rem;
  font-weight: 300;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.lock-screen__label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  margin-bottom: 8px;
  opacity: 0.75;
}

.lock-screen__users {
  display: grid;
  // auto-fit схлопывает пустые колонки: две плитки встают по центру, а не прижимаются влево.
  grid-template-columns: repeat(auto-fit, 124px);
  justify-content: center;
  gap: 8px;
  max-height: 232px;
  overflow-y: auto;
  padding: 2px;
}

.lock-screen__search {
  margin-bottom: 8px;
}

.lock-screen__users--list {
  display: flex;
  flex-direction: column;
  // justify-content: center от плиток в колонке центрировал бы строки по вертикали — верхние
  // уходили бы за край без возможности доскроллить.
  justify-content: flex-start;
  gap: 4px;
  max-height: 216px;
}

.lock-screen__empty {
  padding: 16px 8px;
  font-size: 0.875rem;
  text-align: center;
  opacity: 0.7;
}

.lock-user.v-btn {
  height: auto !important;
  min-height: 88px;
  padding: 10px 8px !important;
  text-transform: none;
  letter-spacing: normal;
  border-radius: 8px;
  justify-content: center;
}

.lock-user:not(.lock-user--selected).v-btn {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.theme--dark .lock-user:not(.lock-user--selected).v-btn {
  background-color: rgba(255, 255, 255, 0.06) !important;
}

.lock-user--selected.v-btn {
  border-width: 2px;
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.lock-user__inner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
}

.lock-user--row.v-btn {
  min-height: 48px;
  padding: 6px 10px !important;
  justify-content: flex-start;
}

.lock-user--row .lock-user__inner {
  flex-direction: row;
  gap: 12px;
}

.lock-user--row .lock-user__check {
  position: static;
  margin-left: auto;
}

.lock-user__initials {
  font-size: 0.9375rem;
  font-weight: 600;
}

.lock-user__name {
  max-width: 100%;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lock-user__check {
  position: absolute;
  top: -4px;
  right: -2px;
}

.lock-screen__form {
  margin-top: 20px;
}

.lock-pin {
  position: relative;
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 4px 0 6px;
  cursor: text;
}

.lock-pin__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  border: 0;
  padding: 0;
  caret-color: transparent;
  cursor: text;
  z-index: 1;
  -webkit-text-security: disc; // type=text ради менеджеров паролей — символы всё равно скрыты
}

.lock-pin__slot {
  width: 48px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  transition: border-color 0.15s ease-out, box-shadow 0.15s ease-out, opacity 0.15s ease-out;
}

.theme--dark .lock-pin__slot {
  border-color: rgba(255, 255, 255, 0.38);
}

.lock-pin__slot--active {
  border: 2px solid #1976d2;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
}

.lock-pin__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: currentColor;
}

.lock-pin--error .lock-pin__slot {
  border: 2px solid #f44336;
  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.15);
}

.lock-pin--error {
  animation: lockShake 0.45s cubic-bezier(0.36, 0.07, 0.19, 0.97);
}

.lock-pin--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.lock-screen__error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  margin-top: 6px;
  font-size: 0.875rem;
  color: #d32f2f;
  text-align: center;
}

.theme--dark .lock-screen__error {
  color: #ef9a9a;
}

// Цифровая клавиатура — для сенсорных экранов (планшет на ресепшене); с мышью и клавиатурой PIN
// набирают с клавиатуры, а карточка остаётся в высоту ноутбука.
@media (hover: hover) and (pointer: fine) {
  .lock-keypad {
    display: none !important;
  }

  .lock-screen__form .v-btn--block {
    margin-top: 12px;
  }
}

.lock-keypad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin: 14px auto 16px;
  max-width: 264px;
}

.lock-keypad__key.v-btn {
  height: 48px !important;
  min-width: 0 !important;
  font-size: 1.25rem;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  border-radius: 8px;
}

.lock-keypad__key--blank.v-btn {
  visibility: hidden;
}

.lock-screen__footer {
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 16px 14px;
}

.lock-screen__forgot {
  margin-top: 4px;
  text-align: center;
  font-size: 0.875rem;
}

@keyframes lockShake {
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 50%, 70% { transform: translateX(-4px); }
  40%, 60% { transform: translateX(4px); }
}

@media (prefers-reduced-motion: reduce) {
  .lock-pin--error {
    animation: none;
  }
}
</style>
